import React from "react";
import { Card } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { User, Building, Crown, Clock, Globe, Briefcase, Users, CreditCard, ShieldCheck } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

const planBadge = {
    paid: { label: "Paid", className: "bg-green-100 text-green-700" },
    trial: { label: "Trial", className: "bg-amber-100 text-amber-700" },
    none: { label: "No Plan", className: "bg-gray-100 text-gray-600" },
};

export default function TicketDetailSidebar({ ticket }) {
    const user = ticket.createdBy;
    const company = ticket.company;
    const plan = planBadge[company?.plan] || planBadge.none;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 lg:w-72 lg:flex-shrink-0">
            {user && <UserCard user={user} />}
            {company && <CompanyCard company={company} plan={plan} />}
        </div>
    );
}

function UserCard({ user }) {
    return (
        <Card className="p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Customer</span>
            </div>
            <div className="space-y-1.5 sm:space-y-2 text-sm">
                <p className="font-medium">{user.firstName} {user.lastName}</p>
                <p className="text-muted-foreground truncate">{user.email}</p>
                {user.role && <InfoRow icon={Briefcase} value={user.role} />}
                {user.industry && <InfoRow icon={Building} value={user.industry} />}
                {user.language && <InfoRow icon={Globe} value={user.language} />}
                {user.buildingGoal && <InfoRow icon={Crown} value={user.buildingGoal} />}
                {user.lastLogin && (
                    <InfoRow icon={Clock} value={`Active ${formatDistanceToNow(new Date(user.lastLogin), { addSuffix: true })}`} />
                )}
            </div>
        </Card>
    );
}

function CompanyCard({ company, plan }) {
    return (
        <Card className="p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <Building className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Company</span>
            </div>
            <div className="space-y-1.5 sm:space-y-2 text-sm">
                <p className="font-medium">{company.name}</p>
                <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={plan.className}>{plan.label}</Badge>
                    {company.plan === "trial" && company.trialEndDate && (
                        <span className="text-xs text-muted-foreground">
                            ends {format(new Date(company.trialEndDate), "MMM d")}
                        </span>
                    )}
                </div>
                {company.subscription?.cancelAtPeriodEnd && (
                    <p className="text-xs text-red-500">Cancels at period end</p>
                )}
                <InfoRow icon={Users} value={`${company.userCount} user${company.userCount !== 1 ? "s" : ""}`} />
                {company.supportPlan ? (
                    <InfoRow icon={ShieldCheck} value={`${company.supportPlan.tier} (${company.supportPlan.remainingHours}/${company.supportPlan.totalHours}h)`} />
                ) : (
                    <InfoRow icon={ShieldCheck} value="No support plan" muted />
                )}
                {company.createdAt && (
                    <InfoRow icon={CreditCard} value={`Joined ${format(new Date(company.createdAt), "MMM yyyy")}`} />
                )}
            </div>
        </Card>
    );
}

function InfoRow({ icon: Icon, value, muted }) {
    return (
        <div className="flex items-center gap-2">
            <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <span className={`${muted ? "text-muted-foreground" : ""} break-words`}>{value}</span>
        </div>
    );
}
